import { FormInstance } from 'antd';
import React, { createContext, useContext, useState } from 'react';
import {
  FinancialDocumentAllocation,
  IncomingInvoiceDiff,
  IncomingInvoiceDto,
} from '../../../../../../models/IncomingInvoices';

interface IncomingInvoiceDrawerContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  unsavedChangesModalVisible: boolean;
  setUnsavedChangesModalVisible: (value: boolean) => void;
  form?: FormInstance<IncomingInvoiceDto>;
  setForm?: (form: FormInstance<IncomingInvoiceDto>) => void;
  differences: IncomingInvoiceDiff;
  setDifferences: (differences: IncomingInvoiceDiff) => void;
  differenceModalVisible: boolean;
  setDifferenceModalVisible: (value: boolean) => void;
  initialAllocations?: Partial<FinancialDocumentAllocation>[];
  setInitialAllocations?: (
    allocations: Partial<FinancialDocumentAllocation>[]
  ) => void;
  invoiceAllocations?: Partial<FinancialDocumentAllocation>[];
  setInvoiceAllocations?: (
    allocations: Partial<FinancialDocumentAllocation>[]
  ) => void;
  financialDocumentId: string;
  setFinancialDocumentId: (id: string) => void;
  isDirtyAllocations: boolean;
  setIsDirtyAllocations: (isDirty: boolean) => void;
  invoiceAmountAllocated: boolean;
  setInvoiceAmountAllocated: (isFullyAllocated: boolean) => void;
  allocationsValid: boolean;
  setAllocationsValid: (allocationValid: boolean) => void;
}

const IncomingInvoiceDrawerContext = createContext<
  IncomingInvoiceDrawerContextType | undefined
>(undefined);

export const IncomingInvoiceDrawerContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedChangesModalVisible, setUnsavedChangesModalVisible] =
    useState(false);
  const [form, setForm] = useState<FormInstance<IncomingInvoiceDto>>();
  const [differences, setDifferences] = useState<IncomingInvoiceDiff | null>(
    null
  );
  const [differenceModalVisible, setDifferenceModalVisible] = useState(false);

  const [invoiceAllocations, setInvoiceAllocations] = useState<
    Partial<FinancialDocumentAllocation>[]
  >([]);
  const [initialAllocations, setInitialAllocations] = useState<
    Partial<FinancialDocumentAllocation>[]
  >([]);
  const [isDirtyAllocations, setIsDirtyAllocations] = useState(false);

  const [invoiceAmountAllocated, setInvoiceAmountAllocated] = useState(false);
  const [allocationsValid, setAllocationsValid] = useState(false);

  const [financialDocumentId, setFinancialDocumentId] = useState<string>('');

  return (
    <IncomingInvoiceDrawerContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        unsavedChangesModalVisible,
        setUnsavedChangesModalVisible,
        form,
        setForm,
        differences,
        setDifferences,
        differenceModalVisible,
        setDifferenceModalVisible,
        initialAllocations,
        setInitialAllocations,
        invoiceAllocations,
        setInvoiceAllocations,
        financialDocumentId,
        setFinancialDocumentId,
        isDirtyAllocations,
        setIsDirtyAllocations,
        invoiceAmountAllocated,
        setInvoiceAmountAllocated,
        allocationsValid,
        setAllocationsValid,
      }}
    >
      {children}
    </IncomingInvoiceDrawerContext.Provider>
  );
};

export const useIncomingInvoiceDrawerContext = () => {
  const context = useContext(IncomingInvoiceDrawerContext);
  if (!context)
    throw new Error(
      'useIncomingInvoiceDrawerContext must be used within IncomingInvoiceDrawerProvider'
    );
  return context;
};
